import { useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Reply, Send, X, Flame } from "lucide-react";
import BadgeChip, { useBadges } from "./BadgeChip";

export default function CommentThread({ comments, onSubmit, onFlame, user, openAuth, busy }) {
  const [replyTo, setReplyTo] = useState(null);
  const [draft, setDraft] = useState("");
  const badges = useBadges();

  const byParent = {};
  comments.forEach((c) => {
    const p = c.parent_id || "root";
    (byParent[p] = byParent[p] || []).push(c);
  });

  const submitReply = async (e, parentId) => {
    e.preventDefault();
    if (!draft.trim()) return;
    const ok = await onSubmit(parentId, draft.trim());
    if (ok !== false) {
      setReplyTo(null);
      setDraft("");
    }
  };

  const renderNode = (c, depth) => (
    <div key={c.id} className={depth > 0 ? "mt-4 ml-4 lg:ml-8 border-l-2 border-kinetic/25 pl-4" : ""}>
      <div data-testid="comment-item" className="border border-white/10 bg-surface p-5">
        <div className="flex items-center gap-3 mb-3">
          <span className="w-8 h-8 rounded-full bg-elevate border border-white/10 flex items-center justify-center font-display font-bold text-xs text-kinetic shrink-0">
            {c.author_name.slice(0, 2).toUpperCase()}
          </span>
          <div className="flex-1 min-w-0">
            <Link to={`/member/${c.author_id}`} data-testid="comment-author-link" className="text-sm font-medium hover:text-kinetic transition-colors">
              {c.author_name}
            </Link>
            <BadgeChip badge={(badges[c.author_id] || [])[0]} />
            <p className="text-[10px] font-mono text-ash uppercase tracking-[0.15em]">{format(new Date(c.created_at), "dd MMM yyyy · HH:mm")}</p>
          </div>
          <button
            data-testid={`comment-flame-button-${c.id}`}
            onClick={() => onFlame(c)}
            title={user ? (c.flamed ? "Take back your flame" : "Give a flame") : "Sign in to flame"}
            className={`flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] border rounded-full px-3 py-1.5 transition-colors shrink-0 ${c.flamed ? "text-kinetic border-kinetic/50" : "text-ash hover:text-kinetic border-white/10 hover:border-kinetic/50"}`}>
            <Flame size={11} fill={c.flamed ? "currentColor" : "none"} />
            {(c.flames || 0) > 0 && c.flames}
          </button>
          <button
            data-testid="comment-reply-button"
            onClick={() => {
              if (!user) { openAuth("login"); return; }
              setReplyTo(replyTo === c.id ? null : c.id);
              setDraft("");
            }}
            className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-ash hover:text-kinetic border border-white/10 hover:border-kinetic/50 rounded-full px-3 py-1.5 transition-colors shrink-0">
            <Reply size={11} /> Reply
          </button>
        </div>
        <p className="text-sm text-porcelain/85 leading-relaxed">{c.body}</p>
      </div>
      {replyTo === c.id && (
        <form onSubmit={(e) => submitReply(e, c.id)} className="flex gap-2 mt-3">
          <input
            data-testid="comment-reply-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={2000}
            placeholder={`Reply to ${c.author_name}…`}
            autoFocus
            className="flex-1 bg-obsidian border border-white/15 focus:border-kinetic/60 outline-none px-4 py-3 text-sm transition-colors"
          />
          <button data-testid="comment-reply-submit-button" type="submit" disabled={busy || !draft.trim()}
            className="bg-kinetic text-obsidian px-4 flex items-center justify-center hover:bg-porcelain transition-colors disabled:opacity-50">
            <Send size={15} />
          </button>
          <button type="button" data-testid="comment-reply-cancel-button" onClick={() => setReplyTo(null)}
            className="px-3 text-ash hover:text-porcelain border border-white/10 transition-colors">
            <X size={15} />
          </button>
        </form>
      )}
      {(byParent[c.id] || []).map((child) => renderNode(child, depth + 1))}
    </div>
  );

  return <div className="space-y-5">{(byParent["root"] || []).map((c) => renderNode(c, 0))}</div>;
}
